import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowForward,
  Assessment,
  PrecisionManufacturing,
  Refresh,
  VpnKey,
  ViewList,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useApp } from "../contexts/AppContext";
import { reportsService } from "../services/api";
import PageHeader from "../components/ui/PageHeader";
import OverviewMetrics from "../components/ui/OverviewMetrics";
import SoftwareOverview from "../components/SoftwareOverview";

const statusLabels = {
  online: "Online",
  offline: "Offline",
  maintenance: "Manutenzione",
  error: "Errore",
};
export default function DashboardPage() {
  const { state, refreshMachines, selectMachine } = useApp();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<{
    hours: number;
    completed: number;
    points: { day: string; minutes: number }[];
  } | null>(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [softwareRefresh, setSoftwareRefresh] = useState(0);
  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const data = await reportsService.getMachinesReport({
        startDate: new Date(Date.now() - 7 * 86400000).toISOString(),
        endDate: new Date().toISOString(),
      });
      setSummary({
        hours: data.summary.productionHoursTotal,
        completed: data.byDay.reduce((sum, d) => sum + d.jobsCompleted, 0),
        points: data.byDay.map((d) => ({
          day: d.day,
          minutes: d.productionMinutes,
        })),
      });
      setUpdatedAt(new Date());
      setError("");
    } catch {
      setError(
        "Il riepilogo di produzione non è disponibile. Riprova ad aggiornare.",
      );
    } finally {
      setRefreshing(false);
    }
  }, []);
  useEffect(() => {
    void load();
    const interval = setInterval(() => {
      void load();
      void refreshMachines();
      setSoftwareRefresh(value => value + 1);
    }, 60000);
    return () => clearInterval(interval);
  }, [load, refreshMachines]);
  const machineMetric = (n: number) =>
    state.machinesError || (state.machinesLoading && !state.machinesUpdatedAt)
      ? "—"
      : n;
  const online = state.machines.filter((m) => m.status === "online").length;
  const producing = state.machines.filter(
    (m) => m.status === "online" && m.mode === "PRODUCTION",
  ).length;
  const attention = state.machines.filter((m) =>
    ["error", "maintenance"].includes(m.status),
  );
  const recentMachines = [...state.machines]
    .sort(
      (a, b) =>
        Number(["error", "maintenance"].includes(b.status)) -
          Number(["error", "maintenance"].includes(a.status)) ||
        a.name.localeCompare(b.name, "it"),
    )
    .slice(0, 6);
  const shortcuts = [
    {
      title: "Licenze software",
      text: "Scadenze, coperture e PC autorizzati",
      path: "/licenses",
      icon: <VpnKey />,
    },
    {
      title: "Parco macchine",
      text: "Connessioni, clienti e licenze HMI",
      path: "/machines",
      icon: <PrecisionManufacturing />,
    },
    {
      title: "Dati di produzione",
      text: "Tempi, pezzi e risultati per macchina",
      path: "/reports",
      icon: <Assessment />,
    },
    {
      title: "Liste di lavoro",
      text: "Programmi e code da inviare alle macchine",
      path: "/worklists",
      icon: <ViewList />,
    },
  ];
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Centro di controllo"
        subtitle={`Benvenuto${state.auth.user?.name ? `, ${state.auth.user.name}` : ""}. Software, clienti e produzione: le attività principali a portata di mano.`}
        actions={
          <Button
            startIcon={<Refresh />}
            disabled={refreshing}
            onClick={() => {
              void refreshMachines();
              void load();
              setSoftwareRefresh(value => value + 1);
            }}
          >
            Aggiorna dati
          </Button>
        }
      />
      <SoftwareOverview refreshKey={softwareRefresh} />
      <Box>
        <Typography variant="h6">Produzione e macchine</Typography>
        <Typography variant="body2" color="text.secondary">Connessioni e attività del parco macchine.</Typography>
      </Box>
      <OverviewMetrics
        items={[
          {
            label: "Parco macchine",
            value: machineMetric(state.machines.length),
            detail: `${online} online · ${state.machines.length - online} non online`,
          },
          {
            label: "In produzione",
            value: machineMetric(producing),
            detail: "Macchine operative adesso",
            color: "success.main",
          },
          {
            label: "Ore di produzione",
            value:
              summary && !error
                ? summary.hours.toLocaleString("it-IT", {
                    maximumFractionDigits: 1,
                  })
                : "—",
            detail: "Ultimi 7 giorni",
          },
          {
            label: "Da verificare",
            value: machineMetric(attention.length),
            detail: "Errori o manutenzione",
            color: attention.length ? "warning.main" : "text.primary",
          },
        ]}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
        }}
      >
        {shortcuts.map((s) => (
          <Card key={s.path} sx={{ boxShadow: "none" }}>
            <CardActionArea
              onClick={() => navigate(s.path)}
              sx={{ height: "100%" }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ color: "primary.main", mb: 1.5 }}>{s.icon}</Box>
                <Typography fontWeight={700}>{s.title}</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {s.text}
                </Typography>
                <ArrowForward
                  sx={{ fontSize: 18, mt: 2, color: "primary.main" }}
                />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
      {state.machinesError && (
        <Alert severity="warning">{state.machinesError}</Alert>
      )}
      {attention.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" onClick={() => navigate("/machines")}>
              Vedi macchine
            </Button>
          }
        >
          {attention.length}{" "}
          {attention.length === 1
            ? "macchina richiede una verifica"
            : "macchine richiedono una verifica"}
          . Apri i dettagli per verificarne lo stato.
        </Alert>
      )}
      {error && <Alert severity="warning">{error}</Alert>}
      <Paper sx={{ p: { xs: 2, md: 3 }, boxShadow: "none" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h6">Andamento della produzione</Typography>
            <Typography variant="body2" color="text.secondary">
              Ultimi 7 giorni · minuti di lavorazione registrati dalle macchine
            </Typography>
          </Box>
          <Button
            endIcon={<ArrowForward />}
            onClick={() => navigate("/reports")}
          >
            Apri analisi
          </Button>
        </Stack>
        {summary && !error && summary.points.length ? (
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={summary.points}
                margin={{ top: 10, right: 16, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient
                    id="production-fill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="var(--accent-blue)"
                      stopOpacity={0.25}
                    />
                    <stop
                      offset="100%"
                      stopColor="var(--accent-blue)"
                      stopOpacity={0.01}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--border-color)" vertical={false} />
                <XAxis
                  dataKey="day"
                  tickFormatter={(d) =>
                    new Date(`${d}T00:00:00Z`).toLocaleDateString("it-IT", {
                      day: "2-digit",
                      month: "2-digit",
                      timeZone: "UTC",
                    })
                  }
                  stroke="var(--text-muted)"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="var(--text-muted)"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--bg-card)",
                    borderColor: "var(--border-color)",
                    borderRadius: 10,
                    color: "var(--text-primary)",
                  }}
                  labelFormatter={(d) =>
                    new Date(`${d}T00:00:00Z`).toLocaleDateString("it-IT", {
                      timeZone: "UTC",
                    })
                  }
                />
                <Area
                  name="Minuti di produzione"
                  type="monotone"
                  dataKey="minutes"
                  stroke="var(--accent-blue)"
                  strokeWidth={2.5}
                  fill="url(#production-fill)"
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography color="text.secondary">
              {refreshing
                ? "Caricamento dati di produzione…"
                : error
                  ? "Dati non disponibili"
                  : "Nessuna lavorazione registrata nel periodo"}
            </Typography>
          </Box>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 2, display: "block" }}
        >
          {summary && !error
            ? `${summary.completed} lavorazioni completate nel periodo. `
            : ""}
          {updatedAt
            ? `Ultimo aggiornamento report: ${updatedAt.toLocaleTimeString("it-IT")}.`
            : ""}{" "}
          Le ore di produzione misurano il tempo di lavorazione, non il tempo di
          connessione.
        </Typography>
      </Paper>
      <Paper sx={{ overflow: "hidden", boxShadow: "none" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2.5 }}
        >
          <Box>
            <Typography variant="h6">Stato delle macchine</Typography>
            <Typography variant="body2" color="text.secondary">
              Prima le macchine da verificare · prime 6
            </Typography>
          </Box>
          <Button onClick={() => navigate("/machines")}>Vedi tutte</Button>
        </Stack>
        <TableContainer>
          <Table sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                {[
                  "Macchina",
                  "Cliente",
                  "Connessione",
                  "Ultimo contatto",
                  "",
                ].map((h, i) => (
                  <TableCell key={i}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {recentMachines.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {m.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.serialNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{m.companyName || "Non assegnato"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={statusLabels[m.status]}
                      color={
                        m.status === "online"
                          ? "success"
                          : m.status === "error"
                            ? "error"
                            : m.status === "maintenance"
                              ? "warning"
                              : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {m.lastSeen
                      ? new Date(m.lastSeen).toLocaleString("it-IT")
                      : "Mai ricevuto"}
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        selectMachine(m);
                        navigate(`/machines/${m.id}`);
                      }}
                      aria-label={`Apri macchina ${m.name}`}
                    >
                      Dettagli
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!recentMachines.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    Nessuna macchina associata al tuo account.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}
